// The staff API client. Every request attaches a staff bearer via api.* — this
// console is entirely authenticated (the public invite flow that once needed
// bearer-less calls now lives in d3-auth).
//
// The backend origin always comes from getBackendUrl() (PUBLIC_API_URL, inlined
// at build time), never window.location.origin: the SPA and the API are on
// different hosts.

import { readErrorBody, readJsonBody } from './api-response.js';
import { authState } from './auth.js';
import { getBackendUrl } from './backend.js';

async function request(path, options = {}) {
    const response = await fetch(`${getBackendUrl()}${path}`, options);

    if (!response.ok) {
        // Every staff endpoint gates on access:staff, so a 403 means exactly "not
        // staff" — demote so the shell shows the not-authorized screen. This is
        // the backstop for a grant revoked mid-session, where the cached token
        // still claims staff until it refreshes; the proactive claim check at
        // sign-in handles the common case.
        if (response.status === 403) {
            authState.markNotStaff();
        }
        throw await readErrorBody(response);
    }

    return await readJsonBody(response);
}

class ApiClient {
    /**
     * Build the auth header. getAccessToken() transparently refreshes a token
     * that is near expiry or stale, so by the time a request goes out the token
     * is fresh. That is why there is no 401-retry loop here: a 401 after a
     * proactive refresh means the session is genuinely dead, and authState has
     * already cleared it and notified. Retrying would only hide that.
     */
    async _headers(extra = {}) {
        const headers = { ...extra };
        try {
            headers.Authorization = `Bearer ${await authState.getAccessToken()}`;
        } catch {
            // Not signed in. Send it unauthenticated and let the server say so,
            // rather than inventing a client-side 401.
        }
        return headers;
    }

    async get(path) {
        return request(path, { headers: await this._headers() });
    }

    async post(path, body) {
        return request(path, {
            method: 'POST',
            headers: await this._headers({ 'Content-Type': 'application/json' }),
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }

    async put(path, body) {
        return request(path, {
            method: 'PUT',
            headers: await this._headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });
    }

    async delete(path) {
        return request(path, { method: 'DELETE', headers: await this._headers() });
    }

    /**
     * POST one file as multipart/form-data under the field name `file`. No
     * Content-Type is set here: the browser adds it, boundary included.
     * @param {string} path
     * @param {File|Blob} file
     * @param {string} filename
     */
    async upload(path, file, filename) {
        const form = new FormData();
        form.append('file', file, filename);
        return request(path, { method: 'POST', headers: await this._headers(), body: form });
    }
}

export const api = new ApiClient();
